/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Card, CardContent, CardExtra } from '../../Shared/Card'
import { Icon, Statistic, StatisticLabel, StatisticValue, Progress, Popup } from 'semantic-ui-react'
import { Link } from 'react-router-dom-v5-compat'
import { useQuery } from '@tanstack/react-query'
import { getMetricsOrders } from '../../../../services/Metrics/Metrics.service'
import { formatISO, roundToNearestMinutes, startOfMonth, subMonths, endOfMonth, format } from 'date-fns'
import { fr } from 'date-fns/locale'
import useUser from '../../../Shared/UserProvider/useUser'
import { isProviderAPos } from '../../../../services/Provider/Provider.service'
import { getCancellationSource } from '../../../../services/http-client'
import { HUNDRED_THOUSAND, intlFormat, intlFormatMoneyWithCompact } from '../../../../helpers/money'
import { LoadingPlaceholderCardContent } from '../../Shared/LoadingPlaceholderCardContent'
import { useTranslation } from 'react-i18next'
import { CardTitle } from '../../Shared/CardTitle'
import { intlFormatNumberWithCompact, TEN_THOUSAND } from '../../../../helpers/number'
import { useDeviceMediaQuery } from '../../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { ValueVariation } from '../../Shared/ValueVariation'
import React from 'react'


const MonthlyPositiveActivityCard = () => {
    const { isMobile } = useDeviceMediaQuery()
    const [t] = useTranslation()
    const {user} = useUser()

    const now = new Date()
    const from = formatISO(startOfMonth(now))
    const to = formatISO(roundToNearestMinutes(now, {nearestTo: 10, roundingMethod: 'floor'}))

    const {data, isLoading} = useQuery({
        queryKey: ['monthly-positive-metrics', from, to],
        queryFn: async () => await getMetricsOrders({
            dateRange: {
                startDate: from,
                endDate: to,
            },
            merchantId: isProviderAPos(user.provider) ? user.provider.object.id : null,
            brandIds: [],
            platformIds: [],
        }, getCancellationSource()),
    })

    const previousMonth = subMonths(now, 1)
    const fromPreviousMonth = formatISO(startOfMonth(previousMonth))
    const toPreviousMonth = formatISO(endOfMonth(previousMonth))

    // Same period of previous month: 1st to same day + same hour
    const samePeriodEnd = subMonths(roundToNearestMinutes(now, {nearestTo: 10, roundingMethod: 'floor'}), 1)
    const fromSamePeriod = formatISO(startOfMonth(previousMonth))
    const toSamePeriod = formatISO(samePeriodEnd)

    const {data: dataComparison} = useQuery({
        queryKey: ['monthly-positive-metrics-previous-month', fromPreviousMonth, toPreviousMonth],
        queryFn: async () => await getMetricsOrders({
            dateRange: {
                startDate: fromPreviousMonth,
                endDate: toPreviousMonth,
            },
            merchantId: isProviderAPos(user.provider) ? user.provider.object.id : null,
            brandIds: [],
            platformIds: [],
        }, getCancellationSource()),
    })

    const {data: dataSamePeriod} = useQuery({
        queryKey: ['monthly-positive-metrics-previous-month-same-period', fromSamePeriod, toSamePeriod],
        queryFn: async () => await getMetricsOrders({
            dateRange: {
                startDate: fromSamePeriod,
                endDate: toSamePeriod,
            },
            merchantId: isProviderAPos(user.provider) ? user.provider.object.id : null,
            brandIds: [],
            platformIds: [],
        }, getCancellationSource()),
    })

    const nbOrder = data?.nbOrders.reduce((total, nbOrder) => total + nbOrder.y, 0) || 0
    const revenues = data?.revenues.reduce((total, revenue) => total + revenue.y, 0) || 0
    const averageOrder = Math.round(data?.avgOrders.reduce((total, avgOrder) => total + avgOrder.y, 0) / data?.avgOrders.length) || 0

    const revenuesComparison = dataComparison?.revenues.reduce((total, revenue) => total + revenue.y, 0) || 0
    const revenuesSamePeriod = dataSamePeriod?.revenues.reduce((total, revenue) => total + revenue.y, 0) || 0
    const nbOrderSamePeriod = dataSamePeriod?.nbOrders.reduce((total, nbOrder) => total + nbOrder.y, 0) || 0

    const revenueProgress = revenuesComparison > 0
      ? (revenues / revenuesComparison) * 100
      : (revenues > 0 ? 100 : 0)

    return (
      <Card
        css={css`
            border-top: 2px solid ${isLoading ? 'unset' : '#21ba45'};

            .card-content {
                width: ${isMobile ? '340px' : '360px'};
                height: 350px;
            }
        `}
      >
          {isLoading ? (
            <LoadingPlaceholderCardContent/>
          ) : (
            <CardContent
              css={css`
                  display: grid;
                  place-items: center;
                  gap: .5rem;
                  grid-template-areas: 'title title' 'b b' 'c d' 'progress progress';
                  grid-template-rows: 30px repeat(2, 1fr) 100px;
              `}
            >
                <>
                    <CardTitle>
                        <div
                          css={css`
                                display: flex;
                                gap: 0.25rem;

                                i {
                                    margin-top: .3rem;
                                }
                            `}
                        >
                            <span>{format(now, 'MMMM', {locale: fr})}</span>
                            <Popup
                              trigger={<Icon name="info circle" size={'small'}/>}
                              content={t('dashboard.monthly_positive_activity.info_label', { month: format(previousMonth, 'MMMM', {locale: fr}) })}
                            />
                        </div>
                    </CardTitle>

                    <Statistic
                      css={css`
                          grid-area: b;

                          &.ui.statistic, &.ui.statistic + .ui.statistic {
                              margin: 0;
                          }
                      `}
                      size="tiny"
                    >
                        <StatisticValue>
                            {intlFormatMoneyWithCompact(revenues, user.getCompanyCurrency(), user.getCompanyLocale(), HUNDRED_THOUSAND)}
                            <ValueVariation
                                value={revenues}
                                referenceValue={revenuesSamePeriod}
                                formatter={(value) => intlFormatMoneyWithCompact(value, user.getCompanyCurrency(), user.getCompanyLocale(), HUNDRED_THOUSAND)}
                            />
                        </StatisticValue>
                        <StatisticLabel>
                            {t('home.revenues')}
                        </StatisticLabel>
                    </Statistic>

                    <Statistic
                      css={css`
                          grid-area: c;

                          &.ui.statistic + .ui.statistic {
                              margin: 0;
                          }
                      `}
                      size="tiny"
                    >
                        <StatisticValue>
                            {intlFormatNumberWithCompact(nbOrder, user.getCompanyLocale(), TEN_THOUSAND)}
                            <ValueVariation
                                value={nbOrder}
                                referenceValue={nbOrderSamePeriod}
                                formatter={(value) => intlFormatNumberWithCompact(value, user.getCompanyLocale(), TEN_THOUSAND)}
                            />
                        </StatisticValue>
                        <StatisticLabel>
                            {t('home.number_order', {count: nbOrder})}
                        </StatisticLabel>
                    </Statistic>

                    <Statistic
                      css={css`
                          grid-area: d;
                      `}
                      size="tiny"
                    >
                        <StatisticValue>
                            {intlFormat(averageOrder, user.getCompanyCurrency(), user.getCompanyLocale())}
                            <ValueVariation
                                value={averageOrder}
                                referenceValue={dataSamePeriod?.avgOrders?.length > 0 ? Math.round(dataSamePeriod.avgOrders.reduce((total, avgOrder) => total + avgOrder.y, 0) / dataSamePeriod.avgOrders.length) : null}
                                formatter={(value) => intlFormat(value, user.getCompanyCurrency(), user.getCompanyLocale())}
                            />
                        </StatisticValue>
                        <StatisticLabel>
                            {t('home.average_order')}
                        </StatisticLabel>
                    </Statistic>

                    <div
                      css={css`
                          grid-area: progress;
                          align-self: end;
                          width: 100%;
                          padding: 1rem;
                      `}
                    >
                        {revenueProgress > 100 && (
                          <div
                            css={css`
                                text-align: center;
                                font-size: 1.2rem;
                                font-weight: 700;
                                color: #21ba45;
                                margin-bottom: 0.5rem;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                gap: 0.2rem;
                            `}
                          >
                              <Icon name="chart line"/>
                              +{Math.round(revenueProgress - 100)}%
                          </div>
                        )}
                        <Progress
                          percent={Math.round(revenueProgress)}
                          color="green"
                          size="medium"
                          progress={revenueProgress <= 100}
                          css={css`
                              &.ui.progress {
                                  margin-top: 0;
                                  margin-bottom: 0;
                              }
                          `}
                        />
                        <div
                          css={css`
                              text-align: center;
                              font-size: 0.75rem;
                              color: rgba(0, 0, 0, 0.6);
                          `}
                        >
                            {t('dashboard.monthly_positive_activity.progress_label', {value: revenuesComparison > 0 ? intlFormatMoneyWithCompact(revenuesComparison, user.getCompanyCurrency(), user.getCompanyLocale(), HUNDRED_THOUSAND) : '-'})}
                        </div>
                    </div>
                </>
            </CardContent>
          )}


          <CardExtra>
              <Link
                to={{
                    pathname: '/analytics',
                    search: new URLSearchParams({
                        from,
                        to,
                    }).toString(),
                }}>
                  {t('home.revenues')}
                  <Icon name="arrow right"/>
              </Link>
          </CardExtra>
      </Card>
    )
}

export { MonthlyPositiveActivityCard }
